import { useEffect, useState, useMemo } from "react";
import { Loader2, SlidersHorizontal } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StickyCallBar } from "@/components/StickyCallBar";
import { VehicleCard } from "@/components/VehicleCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { api, track } from "@/lib/api";

const SORTS = {
  newest: { label: "Newest first", fn: (a, b) => new Date(b.created_at) - new Date(a.created_at) },
  "price-asc": { label: "Price: Low to High", fn: (a, b) => a.price - b.price },
  "price-desc": { label: "Price: High to Low", fn: (a, b) => b.price - a.price },
  "year-desc": { label: "Year: Newest", fn: (a, b) => b.year - a.year },
  "mileage-asc": { label: "Mileage: Lowest", fn: (a, b) => a.mileage - b.mileage },
};

const roundUp = (n, step) => Math.ceil(n / step) * step;

export default function Inventory() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [make, setMake] = useState("all");
  const [bodyType, setBodyType] = useState("all");
  const [sort, setSort] = useState("newest");
  const [priceRange, setPriceRange] = useState(null); // [min,max] or null=full

  useEffect(() => {
    track("inventory");
    api.get("/vehicles").then((r) => setVehicles(r.data)).finally(() => setLoading(false));
  }, []);

  const saleable = useMemo(() => vehicles.filter((v) => v.status !== "coming_soon"), [vehicles]);
  const makes = useMemo(() => Array.from(new Set(saleable.map((v) => v.make))).sort(), [saleable]);
  const bodyTypes = useMemo(() => Array.from(new Set(saleable.map((v) => v.body_type))).sort(), [saleable]);
  const maxPrice = useMemo(
    () => (saleable.length ? roundUp(Math.max(...saleable.map((v) => v.price)), 1000) : 100000),
    [saleable]
  );
  const range = useMemo(() => priceRange || [0, maxPrice], [priceRange, maxPrice]);

  const filtered = useMemo(() => {
    let list = saleable.filter(
      (v) =>
        (make === "all" || v.make === make) &&
        (bodyType === "all" || v.body_type === bodyType) &&
        v.price >= range[0] &&
        v.price <= range[1]
    );
    return list.sort(SORTS[sort].fn);
  }, [saleable, make, bodyType, sort, range]);

  const resetFilters = () => { setMake("all"); setBodyType("all"); setSort("newest"); setPriceRange(null); };
  const priceActive = priceRange && (priceRange[0] > 0 || priceRange[1] < maxPrice);
  const hasFilters = make !== "all" || bodyType !== "all" || sort !== "newest" || priceActive;

  return (
    <div className="min-h-screen pb-16 md:pb-0" data-testid="inventory-page">
      <Header />
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900">Our Inventory</h1>
          <p className="text-slate-500 mt-2">
            {loading ? "Loading vehicles…" : `${filtered.length} of ${saleable.length} vehicle${saleable.length === 1 ? "" : "s"} for sale`}
          </p>
        </div>
      </div>

      {!loading && saleable.length > 0 && (
        <div className="bg-slate-50 border-b border-slate-200 sticky top-16 z-30" data-testid="inventory-filters">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-3">
            <span className="hidden sm:flex items-center gap-1 text-sm font-semibold text-slate-600">
              <SlidersHorizontal size={16} /> Filter
            </span>
            <Select value={make} onValueChange={setMake}>
              <SelectTrigger className="w-[140px] bg-white" data-testid="filter-make"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Makes</SelectItem>
                {makes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={bodyType} onValueChange={setBodyType}>
              <SelectTrigger className="w-[150px] bg-white" data-testid="filter-body-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Body Types</SelectItem>
                {bodyTypes.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-md px-3 py-2 min-w-[220px]" data-testid="filter-price-range">
              <span className="text-xs font-medium text-slate-600 whitespace-nowrap">
                ${range[0].toLocaleString()} – ${range[1].toLocaleString()}
              </span>
              <Slider
                min={0} max={maxPrice} step={1000}
                value={range}
                onValueChange={(v) => setPriceRange(v)}
                className="w-32"
                data-testid="filter-price-slider"
              />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[170px] bg-white ml-auto" data-testid="filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-slate-500 hover:text-red-600" data-testid="filter-reset">
                Clear
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="flex justify-center py-20 text-slate-400"><Loader2 className="animate-spin" size={32} /></div>
        ) : saleable.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            No vehicles are listed right now. Please call us — new stock arrives often.
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-400" data-testid="no-results">
            No vehicles match your filters.{" "}
            <button onClick={resetFilters} className="text-red-600 font-semibold hover:underline">Clear filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((v, i) => (
              <VehicleCard key={v.id} vehicle={v} index={i} />
            ))}
          </div>
        )}
      </div>

      <Footer />
      <StickyCallBar />
    </div>
  );
}
